// src/components/Reset Password/ResetPasswordForm.tsx

import React, { useState } from 'react';
import styled from 'styled-components';
import { ResetPasswordFormProps, ValidationError } from './resetPasswordTypes';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorMessage } from '../shared/ErrorMessage';

const FormContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: #212529;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const Form = styled.form`
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #495057;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const ErrorMessageStyled = styled(ErrorMessage)`
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 1rem;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
`;

const GlobalError = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
          }
        `;
      case 'secondary':
        return `
          background: #6c757d;
          color: white;
          
          &:hover {
            background: #5a6268;
            transform: translateY(-2px);
          }
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;


const PasswordStrength = styled.div<{ strength: 'weak' | 'medium' | 'strong' }>`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  
  ${props => {
    switch (props.strength) {
      case 'weak':
        return 'color: #dc3545;';
      case 'medium':
        return 'color: #ffc107;';
      case 'strong':
        return 'color: #28a745;';
      default:
        return 'color: #6c757d;';
    }
  }}
`;

const PasswordStrengthBar = styled.div<{ strength: 'weak' | 'medium' | 'strong' }>`
  height: 4px;
  border-radius: 2px;
  margin-top: 0.25rem;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.strength) {
      case 'weak':
        return 'background: #dc3545; width: 33%;';
      case 'medium':
        return 'background: #ffc107; width: 66%;';
      case 'strong':
        return 'background: #28a745; width: 100%;';
      default:
        return 'background: #e9ecef; width: 0%;';
    }
  }}
`;

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onSubmit,
  loading = false,
  error,
  success = false
}) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const validateField = (field: string, value: any): string | null => {
    switch (field) {
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return null;
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return null;
      default:
        return null;
    }
  };

  const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    return 'strong';
  };

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    setValidationErrors(prev => prev.filter(error => error.field !== field));
    
    // Validate field
    const error = validateField(field, value);
    if (error) {
      setValidationErrors(prev => [...prev.filter(e => e.field !== field), { field, message: error }]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const errors: ValidationError[] = [];
    const fields: (keyof typeof formData)[] = ['password', 'confirmPassword'];
    
    fields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors.push({ field, message: error });
      }
    });
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    onSubmit(formData);
  };

  const getFieldError = (field: string) => {
    return validationErrors.find(error => error.field === field)?.message;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  if (success) {
    return (
      <FormContainer>
        <Header>
          <Title>Password Reset Complete</Title>
          <Subtitle>
            Your password has been successfully updated. You can now sign in with your new password.
          </Subtitle>
        </Header>
        
        <Form>
          <SuccessMessage>
            ✅ Password updated successfully! You can now sign in with your new password.
          </SuccessMessage>
          
          <Button
            type="button"
            variant="primary"
            onClick={() => window.location.href = '/login'}
          >
            Go to Login
          </Button>
        </Form>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Header>
        <Title>Reset Your Password</Title>
        <Subtitle>
          Enter your new password below. Make sure it's secure and easy for you to remember.
        </Subtitle>
      </Header>

      {error && <GlobalError>{error}</GlobalError>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="password">New Password *</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleFieldChange('password', e.target.value)}
            placeholder="Enter your new password"
            error={!!getFieldError('password')}
            disabled={loading}
          />
          {formData.password && (
            <>
              <PasswordStrength strength={passwordStrength}>
                Password strength: {passwordStrength}
              </PasswordStrength>
              <PasswordStrengthBar strength={passwordStrength} />
            </>
          )}
          {getFieldError('password') && <ErrorMessageStyled message={getFieldError('password')!} />}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm New Password *</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
            placeholder="Confirm your new password"
            error={!!getFieldError('confirmPassword')}
            disabled={loading}
          />
          {getFieldError('confirmPassword') && <ErrorMessageStyled message={getFieldError('confirmPassword')!} />}
        </FormGroup>

        <Button
          type="submit"
          variant="primary"
          disabled={loading || validationErrors.length > 0}
        >
          {loading && <LoadingSpinner size="small" color="#ffffff" />}
          Update Password
        </Button>
      </Form>
    </FormContainer>
  );
};
